import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccordionSummary, Box, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionStyle } from '../../styles';
import { SummarySubTitle, SummaryTitle } from './style';
import {
  DefectsAreaForCustomBlocksQuery,
  PaintBlocksCorrodedArea,
  PointOfInterestMultiStructureInput,
  PointOfInterestsFindingsByDeckAndPaintBlock,
} from '@/__generated__/graphql';
import { FindingsGraphs } from './FindingsGraphs';
import { DefectsGraph } from './DefectsGraphs';
import { GraphsLoader } from './GraphsLoader';
import { ConditionSelection } from './ConditionSelection';
import { RiskRemidiationChart } from './RiskRemidiationChart';
import { PlatformConditionChartsContainer } from '@/components/Structures/PlatformConditionChartsContainer';
import { useDragToScroll } from '@/hooks';
import { abyssColors } from '@/theme/colors';
import { DueDateWidget } from '../../PointOfInterests/Summary/DueDateWidget';
import { PaintDamageAlarmLevelWidget } from '../../PointOfInterests/Summary/PaintDamageAlarmLevelWidget';
import { StatusWidget } from '../../PointOfInterests/Summary/StatusWidget';
import { useAtomValue } from 'jotai';
import * as state from '@/state';

type Props = {
  activeStructureIds?: string[];
  selectedDeploymentIds?: string[];
  isAtPlatformsPage?: boolean;
  findingsByPaintBlocksAndDecks?: PointOfInterestsFindingsByDeckAndPaintBlock;
  defectAreaForPaintBlocksAndDecks?: PaintBlocksCorrodedArea;
  defectAreaForCustomBlocks?: DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup'];
  selectedPOIType: string[];
  changePOITypeTab: (tab: string) => void;
  changeCoatingTab: (tab: string) => void;
  allCoatingConditions?: DefectLegend[];
  selectedCoatingCondition: string[];
  selectedConditionType: string;
  setSelectedConditionType: React.Dispatch<React.SetStateAction<string>>;
  graphsLoading: boolean;
  coatingConditionLegend: { [key: string]: string };
  defects?: DefectLegend[];
};

type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
};

export const PaintBlockGraph = ({
  selectedDeploymentIds = [],
  coatingConditionLegend,
  selectedPOIType,
  changePOITypeTab,
  changeCoatingTab,
  allCoatingConditions,
  selectedCoatingCondition,
  selectedConditionType,
  setSelectedConditionType,
  graphsLoading,
  defects,
  findingsByPaintBlocksAndDecks,
  defectAreaForPaintBlocksAndDecks,
  defectAreaForCustomBlocks,
  activeStructureIds = [],
  isAtPlatformsPage,
}: Props) => {
  const [graphsContainerWidth, setGraphsContainerWidth] = useState<number>(0);
  const graphsContainerReference = useRef<HTMLDivElement>(null);
  const hasRiskRemediationData = useAtomValue(state.hasRiskRemediationData);
  useEffect(() => {
    if (graphsContainerReference.current) {
      const container = graphsContainerReference.current;
      const resizeObserver = new ResizeObserver(() => {
        setGraphsContainerWidth(container.clientWidth);
      });
      resizeObserver.observe(container);
      return () => {
        resizeObserver.unobserve(container);
      };
    }
    return undefined;
  }, []);

  // AnomalyTracker component for paint damage tracking
  const AnomalyTracker = ({ activeStructureIds }: { activeStructureIds: string[] }) => {
    const [stackWidth, setStackWidth] = useState<number>(0);
    const { containerReference } = useDragToScroll();

    const queryInput: PointOfInterestMultiStructureInput = useMemo(
      () => ({
        structureIds: activeStructureIds,
        type: 'PAINT_DAMAGE',
      }),
      [activeStructureIds]
    );

    const widgetWidth = useMemo(() => {
      if (stackWidth === 0) return 0;
      return `calc((${stackWidth}px - 5.6rem) / 3)`;
    }, [stackWidth]);

    useEffect(() => {
      const currentContainer = containerReference.current;

      const resizeObserver = new ResizeObserver(() => {
        if (currentContainer?.offsetWidth) setStackWidth(currentContainer.offsetWidth);
      });

      if (currentContainer) resizeObserver.observe(currentContainer);

      return () => {
        if (currentContainer) resizeObserver.unobserve(currentContainer);
      };
    }, [containerReference]);

    return (
      <AccordionStyle
        disableGutters
        defaultExpanded
        elevation={0}
        sx={{
          marginTop: '2.4rem !important',
          marginBottom: '2.4rem !important',
          padding: '1.6rem 1.6rem 3.2rem 3.0rem !important',
        }}
      >
        <AccordionSummary
          sx={{ padding: '0px !important', minHeight: 'fit-content' }}
          expandIcon={<ExpandMoreIcon />}
        >
          <SummaryTitle
            sx={{
              fontWeight: 700,
              marginBottom: '1.6rem !important',
              color: abyssColors.primary[500],
            }}
          >
            Anomaly Tracker
          </SummaryTitle>
        </AccordionSummary>
        <Box
          ref={containerReference}
          sx={{
            marginLeft: '0.8rem',
            marginRight: '2.2rem',
          }}
        >
          <Stack direction="row" alignItems="center" gap="2.4rem" width="fit-content">
            <StatusWidget queryInput={queryInput} widgetWidth={widgetWidth} />
            <DueDateWidget
              queryInput={queryInput}
              widgetWidth={widgetWidth}
              legendTextColor={abyssColors.primary[300]}
            />
            <PaintDamageAlarmLevelWidget
              queryInput={queryInput}
              widgetWidth={widgetWidth}
              riskDashboardPage
              legendTextColor={abyssColors.primary[300]}
            />
          </Stack>
        </Box>
      </AccordionStyle>
    );
  };
  return (
    <>
      {isAtPlatformsPage && selectedDeploymentIds?.length > 0 && (
        <>
          <RiskRemidiationChart activeStructureIds={selectedDeploymentIds} />
          <PlatformConditionChartsContainer activeDeploymentIds={selectedDeploymentIds} />
        </>
      )}
      {isAtPlatformsPage && hasRiskRemediationData && selectedDeploymentIds?.length > 0 && (
        <Stack sx={{ marginBottom: !hasRiskRemediationData ? '2.4rem' : '0px' }}>
          <AnomalyTracker activeStructureIds={selectedDeploymentIds} />
        </Stack>
      )}
      {((!hasRiskRemediationData && isAtPlatformsPage) || !isAtPlatformsPage) && (
        <AccordionStyle disableGutters defaultExpanded elevation={0}>
          <AccordionSummary
            sx={{
              padding: '0px !important',
              minHeight: 'fit-content',
            }}
            expandIcon={<ExpandMoreIcon />}
          >
            <SummaryTitle>Summary / charts</SummaryTitle>
          </AccordionSummary>
          <Box ref={graphsContainerReference}>
            {graphsLoading && <GraphsLoader />}

            <ConditionSelection
              defects={defects}
              selectedConditionType={selectedConditionType}
              setSelectedConditionType={setSelectedConditionType}
              selectedCoatingCondition={selectedCoatingCondition}
              changeCoatingTab={changeCoatingTab}
              allCoatingConditions={allCoatingConditions}
            />

            <DefectsGraph
              graphsContainerWidth={graphsContainerWidth}
              defectsAreaForPaintBlockAndDecks={defectAreaForPaintBlocksAndDecks}
              defectAreaForCustomBlocks={defectAreaForCustomBlocks}
              coatingConditionLegend={coatingConditionLegend}
              selectedConditionType={selectedConditionType}
              selectedCoatingCondition={selectedCoatingCondition}
            />
            <SummarySubTitle>Point of Interests</SummarySubTitle>
            <FindingsGraphs
              selectedPOIType={selectedPOIType}
              findingsByPaintBlocksAndDecks={findingsByPaintBlocksAndDecks}
              defectAreaForCustomBlocks={defectAreaForCustomBlocks}
              graphsContainerWidth={graphsContainerWidth}
              changePOITypeTab={changePOITypeTab}
              activeStructureIds={activeStructureIds}
            />
          </Box>
        </AccordionStyle>
      )}
    </>
  );
};
